import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { apiClient } from '../../../helpers/apiHelperV2';
import { toast } from '../../../helpers/toastHelper';
import Button from '../form-elements/Button';
import { standortUrlSelector } from '../../../modules/selectors/url';

function getWiderrufSchema() {
    return Yup.object().shape({
        name: Yup.string().required('Bitte geben Sie Ihren Namen ein.'),
        orderNumber: Yup.string().required('Bitte geben Sie Ihre Auftragsnummer ein.'),
        email: Yup.string()
            .email('Bitte geben Sie eine gültige E-Mail-Adresse ein.')
            .required('Bitte geben Sie Ihre E-Mail-Adresse ein.'),
        notes: Yup.string(),
        reason: Yup.string(),
    });
}

// Field order is used to decide which invalid field receives focus after a failed submit.
const FIELD_ORDER = [
    ['name', 'widerruf-name'],
    ['orderNumber', 'widerruf-orderNumber'],
    ['email', 'widerruf-email'],
];

function FocusFirstError({ submitCount, isValidating, errors }) {
    useEffect(() => {
        if (submitCount === 0 || isValidating) return;
        const firstInvalid = FIELD_ORDER.find(([fieldName]) => errors?.[fieldName]);
        if (firstInvalid) {
            const el = document.getElementById(firstInvalid[1]);
            if (el) el.focus();
        }
    }, [submitCount, isValidating, errors]);

    return null;
}

function FieldError({ name, id }) {
    return (
        <ErrorMessage name={name}>
            {message => (
                <div id={id} role="alert" className="uk-text-danger uk-text-small">
                    {message}
                </div>
            )}
        </ErrorMessage>
    );
}

function WiderrufForm({ initialValues, closeModal, onSubmitSuccess }) {
    const standortUrl = useSelector(standortUrlSelector);
    const focusRef = useRef(null);

    useEffect(() => {
        if (focusRef.current) focusRef.current.focus();
    }, []);

    const defaults = {
        name: '',
        orderNumber: '',
        email: '',
        notes: '',
        reason: '',
        standortUrl: standortUrl || 'de',
        ...initialValues,
    };

    const handleSubmit = async (values, actions) => {
        try {
            const { name, orderNumber, email, notes, reason, productId, orderId, standortUrl } = values;
            await apiClient.post('/api/v1/contractWithdrawal', {
                name,
                orderNumber,
                email,
                notes,
                reason,
                productId,
                standortUrl,
            });
            closeModal();
            toast({ message: 'Ihr Widerruf wurde übermittelt und wird geprüft.', status: 'success' });
            onSubmitSuccess?.({ orderId, productId });
        } catch (e) {
            closeModal();
            toast({
                err: e,
                message: 'Ihre Nachricht konnte nicht gesendet werden, bitte versuchen Sie es später erneut.',
            });
        } finally {
            actions.setSubmitting(false);
        }
    };

    return (
        <Formik
            initialValues={defaults}
            validationSchema={getWiderrufSchema()}
            validateOnChange={false}
            validateOnBlur
            onSubmit={handleSubmit}
        >
            {({ isSubmitting, errors, touched, submitCount, isValidating }) => (
                <Form className="uk-form-stacked widerruf-form" noValidate>
                    <FocusFirstError submitCount={submitCount} isValidating={isValidating} errors={errors} />
                    <div className="uk-modal-header uk-flex uk-flex-between uk-flex-middle">
                        <h2 className="uk-modal-title uk-margin-remove">Vertrag widerrufen</h2>
                        <Button
                            modifier="text"
                            className="uk-modal-close-default"
                            onClick={closeModal}
                            ariaLabel="Modal schließen"
                        >
                            <span data-uk-icon="icon: close" focusable="false" aria-hidden="true" />
                        </Button>
                    </div>

                    <div className="uk-modal-body">
                        <div className="uk-margin">
                            <label className="uk-form-label" htmlFor="widerruf-name">
                                Name: <span className="uk-text-danger">*</span>
                            </label>
                            <div className="uk-form-controls">
                            <Field
                                id="widerruf-name"
                                name="name"
                                type="text"
                                className="uk-input"
                                placeholder="Max Mustermann"
                                innerRef={focusRef}
                                required
                                aria-required="true"
                                aria-invalid={Boolean(touched?.name && errors?.name)}
                                aria-describedby="widerruf-name-error"
                            />
                            </div>
                            <FieldError name="name" id="widerruf-name-error" />
                        </div>

                        <div className="uk-margin">
                            <label className="uk-form-label" htmlFor="widerruf-orderNumber">
                                Hiermit widerrufe ich den Vertrag mit der Auftragsnummer: <span className="uk-text-danger">*</span>
                            </label>
                            <div className="uk-form-controls">
                            <Field
                                id="widerruf-orderNumber"
                                name="orderNumber"
                                type="text"
                                className="uk-input"
                                placeholder="z.B. 25489"
                                required
                                aria-required="true"
                                aria-invalid={Boolean(touched?.orderNumber && errors?.orderNumber)}
                                aria-describedby="widerruf-orderNumber-error"
                            />
                            </div>
                            <FieldError name="orderNumber" id="widerruf-orderNumber-error" />
                        </div>

                        <div className="uk-margin">
                            <label className="uk-form-label" htmlFor="widerruf-email">
                                E-Mail-Adresse: <span className="uk-text-danger">*</span>
                            </label>
                            <div className="uk-form-controls">
                            <Field
                                id="widerruf-email"
                                name="email"
                                type="email"
                                className="uk-input"
                                placeholder="max@beispiel.de"
                                required
                                aria-required="true"
                                aria-invalid={Boolean(touched?.email && errors?.email)}
                                aria-describedby="widerruf-email-error"
                            />
                            </div>
                            <FieldError name="email" id="widerruf-email-error" />
                        </div>

                        <div className="uk-margin">
                            <label className="uk-form-label" htmlFor="widerruf-notes">
                                Hinweise:
                            </label>
                            <div className="uk-form-controls">
                            <Field
                                id="widerruf-notes"
                                name="notes"
                                component="textarea"
                                className="uk-textarea"
                                rows={4}
                                placeholder="Ich möchte nur folgenden Artikel aus der Bestellung widerrufen: Artikel xyz"
                                aria-invalid={Boolean(touched?.notes && errors?.notes)}
                                aria-describedby="widerruf-notes-error"
                            />
                            </div>
                            <FieldError name="notes" id="widerruf-notes-error" />
                        </div>

                        <p className="uk-text-small uk-margin-remove-bottom">*Pflichtfeld</p>

                        <div className="uk-modal-footer uk-text-right uk-padding-remove-horizontal">
                            <Button type="submit" disabled={isSubmitting} fullWidth className="widerruf-submit-button">
                                WIDERRUF BESTÄTIGEN
                            </Button>
                        </div>
                    </div>
                </Form>
            )}
        </Formik>
    );
}

export default WiderrufForm;
